<template>
  <div class="app-page__container app-page__container--no-sidebar">
    <div v-if="!isLoading">
      <h5 class="title is-5">
        Create Team
      </h5>
      <div class="card">
        <div class="card-content">
          <form @submit.prevent="handleSubmitCreateTeam">
            <b-field
              :label="createTeamForm.teamName.label"
              :type="formErrors[createTeamForm.teamName.name] ? 'is-danger' : ''"
              :message="formErrors[createTeamForm.teamName.name] || ''"
            >
              <b-input
                v-model="createTeamForm.teamName.value"
                :disabled="isFormSubmissionInProgress"
                @input="handleTouchForm"
              />
            </b-field>
            <!-- STANDUP DAYS -->
            <b-field :label="createTeamForm.standupMeetingDays.label">
              <div class="columns is-mobile is-marginless">
                <div
                  v-for="(day, index) in TimeAndDateConstants.daysOfTheWeek"
                  :key="day.id"
                  :class="[
                    'column',
                    'u-p-y-0',
                    {'u-p-l-0': index === 0},
                    {'u-p-r-0': index === 6},
                    'has-text-centered',
                    'weekday-picker__weekday-column'
                  ]"
                >
                  <div
                    :class="[
                      'button',
                      'is-fullwidth',
                      createTeamForm.standupMeetingDays.value.includes(day.id) ? 'is-primary' : 'is-outlined',
                      'has-text-weight-bold',
                      'weekday-picker__weekday-column__text-wrapper'
                    ]"
                    role="checkbox"
                    :aria-checked="createTeamForm.standupMeetingDays.value.includes(day.id)"
                    tabindex="0"
                    @click.prevent="handleSelectStandupDay(day.id)"
                    @keyup.enter="handleSelectStandupDay(day.id)"
                  >
                    {{ day.abbreviation }}
                  </div>
                </div>
              </div>
            </b-field>

            <!-- STANDUP TIME -->
            <div class="field columns u-m-y-0">
              <b-field :label="createTeamForm.standupMeetingTime.label" class="column u-p-y-0">
                <b-select name="standupTime" v-model="createTeamForm.standupMeetingTime.value" expanded>
                  <option
                    v-for="hour in TimeAndDateConstants.businessHours"
                    :value="hour.id"
                    :key="hour.id"
                  >
                    {{ hour.label }}
                  </option>
                </b-select>
              </b-field>
              <b-field :label="createTeamForm.timeZone.label" class="column u-p-y-0 u-m-b">
                <b-select name="teamTimeZone" v-model="createTeamForm.timeZone.value" expanded>
                  <option
                    v-for="timeZone in TimeAndDateConstants.timeZones"
                    :value="timeZone.id"
                    :key="timeZone.id"
                  >
                    {{ `${timeZone.abbreviation} ${timeZone.area}/${timeZone.location} ${timeZone.utcOffset}` }}
                  </option>
                </b-select>
              </b-field>
            </div>

            <!-- STANDUP FORMAT -->
            <b-field :label="createTeamForm.standupReportingDays.label" :addons="false">
              <b-field
                v-for="reportingDay in TimeAndDateConstants.standupReportingDays"
                :key="reportingDay.id"
                class="u-m-b-small"
              >
                <b-checkbox
                  v-model="createTeamForm.standupReportingDays.value"
                  :native-value="reportingDay.id"
                  :disabled="createTeamForm.standupReportingDays.value === 1
                    && createTeamForm.standupReportingDays.value.includes(reportingDay.id)"
                >
                  {{ titleCase(reportingDay.label) }}
                  <span class="has-text-grey is-size-7">
                    &nbsp;
                    {{ reportingDay.isRecommended ? 'Recommended for most teams' : '' }}
                  </span>
                </b-checkbox>
              </b-field>
            </b-field>
            <b-field :label="createTeamForm.isPrivate.label" class="u-m-b-small">
              <b-switch
                size="is-small"
                v-model="createTeamForm.isPrivate.value"
              >
                <span class="has-text-grey is-size-7">
                  &nbsp;
                  {{ 'Not recommended for most teams. Only for teams handling sensitive material.' }}
                </span>
              </b-switch>
            </b-field>
            <b-field class="has-text-right u-p-t">
              <b-button
                type="is-info"
                native-type="submit"
                :loading="isFormSubmissionInProgress"
              >
                Create Team
              </b-button>
            </b-field>
          </form>
        </div>
      </div>
    </div>
    <div v-else style="position: relative; min-height: 150px;">
      <b-loading :is-full-page="false" :active.sync="isLoading" :can-cancel="false"></b-loading>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as TeamActions from '@/store/actions/Team.actions';
import * as ValidationService from '@/services/Validation.service';
import * as TimeAndDateConstants from '@/constants/TimeAndDateConstants';
import { isObjectEmpty } from '@/helpers/dataHelpers';
import { titleCase } from '@/helpers/stringHelpers';
import { confirmLeaveWithUnsavedChanges } from '@/helpers/formHelpers';
import { getUserTeams, DEFAULT_TEAM } from '@/helpers/teamHelpers';


export default {
  name: 'CreateTeamView',

  data() {
    return {
      TimeAndDateConstants,
      createTeamForm: {
        teamName: {
          label: 'Team Name',
          name: 'Team Name',
          value: '',
          required: true,
        },
        standupMeetingDays: {
          label: 'Standup Meeting Days',
          name: 'Standup Meeting Days',
          value: null,
          required: true,
        },
        standupReportingDays: {
          label: 'Standup Reporting Days',
          name: 'Standup Reporting Days',
          value: null,
          required: true,
        },
        standupMeetingTime: {
          label: 'Standup Meeting Time',
          name: 'Standup Meeting Time',
          value: null,
          required: true,
        },
        timeZone: {
          label: 'Time Zone',
          name: 'Time Zone',
          value: null,
          required: true,
        },
        isPrivate: {
          label: 'This is a private team',
          name: 'Private Team',
          value: null,
          required: false,
        },
      },
      formErrors: {},
      isFormTouched: true,
      isFormSubmissionInProgress: false,
    };
  },

  computed: {
    ...mapState({
      activeWorkspaceId: state => state.workspace.activeWorkspaceId,
    }),
    ...mapGetters([
      'loggedInUser',
    ]),
    newTeamFormPayload() {
      return {
        teamName: this.createTeamForm.teamName.value,
        standupMeetingDayIds: this.createTeamForm.standupMeetingDays.value,
        standupMeetingTimeId: this.createTeamForm.standupMeetingTime.value,
        standupReportingDayIds: this.createTeamForm.standupReportingDays.value,
        timeZoneId: this.createTeamForm.timeZone.value,
        isPrivate: this.createTeamForm.isPrivate.value,
        slackMeetingReminder: false,
        slackGoalNotification: false,
        workspaceId: this.activeWorkspaceId,
        teamLeaderId: this.loggedInUser.id,
        userIds: [],
      };
    },
    isLoading() {
      return false;
    },
  },

  created() {
    this.setInitialFormState();
  },

  beforeRouteLeave (to, from, next) { // eslint-disable-line
    confirmLeaveWithUnsavedChanges(this.isFormTouched, next);
  },

  methods: {
    titleCase,
    getUserTeams,
    ...mapActions({
      createTeam: TeamActions.CREATE_TEAM,
    }),
    handleClickInviteUser() {
      this.$router.push('/invite-user');
    },
    handleTouchForm() {
      this.isFormTouched = true;
    },
    handleSelectStandupDay(dayOfTheWeekId) {
      if (this.createTeamForm.standupMeetingDays.value.includes(dayOfTheWeekId)) {
        this.createTeamForm.standupMeetingDays.value = [...this.createTeamForm.standupMeetingDays.value.filter(dayId => dayId !== dayOfTheWeekId)]; // eslint-disable-line max-len
      } else {
        this.createTeamForm.standupMeetingDays.value.push(dayOfTheWeekId);
      }
    },
    setInitialFormState() {
      const {
        timeZoneId,
        standupMeetingTimeId,
        standupMeetingDayIds,
        standupReportingDayIds,
        isPrivate,
      } = DEFAULT_TEAM;

      this.createTeamForm.teamName.value = '';
      this.createTeamForm.standupMeetingDays.value = [...standupMeetingDayIds];
      this.createTeamForm.standupReportingDays.value = [...standupReportingDayIds];
      this.createTeamForm.timeZone.value = timeZoneId;
      this.createTeamForm.standupMeetingTime.value = standupMeetingTimeId;
      this.createTeamForm.isPrivate.value = isPrivate;
    },
    async handleSubmitCreateTeam() {
      this.formErrors = ValidationService.runFormValidation({ ...this.createTeamForm });
      if (!isObjectEmpty(this.formErrors)) return;

      this.isFormSubmissionInProgress = true;

      const createdTeamRef = await this.createTeam({ newTeam: { ...this.newTeamFormPayload } });
      this.isFormTouched = false;
      this.$router.push(`/teams/edit/${createdTeamRef.id}`);
      this.isFormSubmissionInProgress = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-team__team-members__title {
  vertical-align: sub;
  display: inline;
  font-weight: bold;
}
</style>
